import { useEffect, forwardRef, useImperativeHandle } from "react"
import { useForm } from "react-hook-form"
import { Box, Button, Heading, Stack } from "@chakra-ui/react"
//
import CheckboxField from "components/forms/fields/CheckboxField"
import Password from "components/forms/fields/Password"
import RadioButtons from "components/forms/fields/RadioButtons"
import ReadOnly from "components/forms/fields/ReadOnly"
import SelectField from "components/forms/fields/SelectField"
import SelectFieldDependent from "components/forms/fields/SelectFieldDependent"
import Spacer from "components/forms/fields/Spacer"
import TextInput from "components/forms/fields/TextInput"
import TextAreaInput from "components/forms/fields/TextAreaInput"
import AutoComplete from "components/forms/fields/AutoComplete"

const Form = forwardRef(({ config, onSubmit, onFieldUpdated, validations }, ref) => {
    // TODO: set up default values as retrieved from backend or Okta here & pass them to the fields in the defaultValues object

    const {
        handleSubmit,
        control,
        setValue,
        getValues,
        trigger,
        setError,
        watch,
        formState: { errors, isSubmitting },
    } = useForm({ mode: "onBlur", defaultValues: {} })

    const fields = {
        checkbox: CheckboxField,
        radio: RadioButtons,
        readonly: ReadOnly,
        select: SelectField,
        select_dependent: SelectFieldDependent,
        spacer: Spacer,
        text: TextInput,
        email: TextInput,
        password: Password,
        textarea: TextAreaInput,
        autocomplete: AutoComplete,
    }

    // expose form functions to parent components
    useImperativeHandle(ref, () => ({
        triggerValidation() {
            return trigger()
        },
        setError(name, value, config = { shouldFocus: true }) {
            return setError(name, value, config)
        },
        setValue(name, value) {
            return setValue(name, value)
        },
        getValues(name) {
            return getValues(name)
        },
        watch(args) {
            return watch(...args)
        },
    }))

    // watch fields
    useEffect(() => {
        const subscription = watch((value, field) => {
            if (watchedFields[field.name]) {
                onFieldUpdated && onFieldUpdated(watchedFields[field.name], setError)
            }
        })
        return () => subscription.unsubscribe()
    }, [watch])

    const watchedFields = {}
    config.sections.forEach(section => {
        section.rows.forEach(row => {
            row.fields.forEach(field => {
                if (field.watch) {
                    // register watched fields
                    watchedFields[field.name] = field
                }
                if (!field.rules) return
                if (!field.rules.validate) field.rules.validate = {}
                Object.keys(field.rules).map(key => {
                    switch (key) {
                        case "equal":
                            field.rules.validate.equal = value => {
                                const check = value === getValues(field.rules[key].field)
                                if (!check) {
                                    // set an error on the other field too?
                                    // setError(field.rules[key].field, {
                                    //     type: "custom",
                                    //     message: field.rules[key].message,
                                    // })
                                    return field.rules[key].message
                                } else {
                                    // clearErrors(field.rules[key].field)
                                    return true
                                }
                            }
                            break
                        case "custom":
                            field.rules.validate.custom = value => {
                                return validations[field.rules[key]](field, value, setError)
                            }
                            break
                        default:
                            break
                    }
                })
            })
        })
    })

    return (
        <Box color="#333">
            <Heading as="h4" fontSize="15px" my={0}>
                {config.title}
            </Heading>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {config.sections.map((section, i) => (
                    <Box key={i} mb={10}>
                        {section.title && (
                            <Heading as="h3" fontSize="22px" mb={3}>
                                {section.title}
                            </Heading>
                        )}
                        {section.rows.map((row, j) => (
                            <Stack key={j} direction={{ base: "column", md: "row" }} gap={{ base: 2, md: 8 }} mb={6}>
                                {row.fields.map(field => {
                                    const FieldComponent = fields[field.type]
                                    if (FieldComponent) {
                                        return (
                                            <FieldComponent
                                                key={field.name}
                                                control={control}
                                                setValue={setValue}
                                                getValues={getValues}
                                                {...field}
                                            />
                                        )
                                    } else {
                                        console.log(`Field type not found: ${field.type}`)
                                        return null
                                    }
                                })}
                            </Stack>
                        ))}
                    </Box>
                ))}
                <Button
                    type="submit"
                    variant="subtle"
                    colorPalette="gray"
                    size="lg"
                    px="25px"
                    fontSize="16px"
                    isLoading={isSubmitting}
                    loadingText="Submitting"
                >
                    {config.submitLabel || "Submit"}
                </Button>
            </form>
        </Box>
    )
})

export default Form
